import { WidgetFactory } from "sparnatural";
import { SparnaturalFormI18n } from "../settings/SparnaturalFormI18n";
import { UnselectBtn } from "sparnatural";
import { Branch, CriteriaLine, SparnaturalQueryIfc } from "sparnatural";
import { ISparnaturalSpecification } from "sparnatural";
import OptionalCriteriaManager from "./optionalCriteria/OptionalCriteriaManager";
import { AbstractWidget, ValueRepetition, I18n } from "sparnatural";
import { Binding, Form } from "../FormStructure";
import tippy from "tippy.js";
import { I18nForm } from "../settings/I18nForm";

class FormField {
  private binding: Binding;
  private formContainer: HTMLElement;
  private specProvider: ISparnaturalSpecification;
  private query: SparnaturalQueryIfc;
  private widgetFactory: WidgetFactory;
  private formConfig: Form;
  private optionalCriteriaManager!: OptionalCriteriaManager; // Optional Criteria Manager instance

  constructor(
    binding: Binding,
    formContainer: HTMLElement,
    specProvider: ISparnaturalSpecification,
    query: SparnaturalQueryIfc,
    widgetFactory: WidgetFactory,
    formConfig: Form,
  ) {
    this.binding = binding;
    this.formContainer = formContainer;
    this.specProvider = specProvider;
    this.query = query;
    this.widgetFactory = widgetFactory;
    this.formConfig = formConfig;
  }

  // Extraire les variables du formulaire depuis formConfig
  private getFormVariables(): string[] {
    return this.formConfig.bindings.map((binding: Binding) => binding.variable);
  }

  generateField(): void {
    const variable = this.binding.variable;

    // Create a container for the field
    const formFieldDiv = document.createElement("div");
    formFieldDiv.classList.add("formField");
    this.formContainer.appendChild(formFieldDiv);

    // Create a label for the widget
    const label = this.createLabel(variable);
    formFieldDiv.appendChild(label);

    // Initialize Tippy.js on the help icon
    setTimeout(() => {
      tippy(".help-icon", {
        allowHTML: true,
        theme: "sparnatural",
        arrow: false,
        placement: "right",
        animation: "scale-extreme",
        delay: [200, 200],
        duration: [200, 200],
      });
    }, 500);

    // Find the line in the query corresponding to the variable
    const queryLine = this.findInBranches(this.query.branches, variable);

    if (queryLine) {
      const widget = this.createWidget(queryLine);
      formFieldDiv.appendChild(widget.html[0]);

      // Initialize OptionalCriteriaManager with formVariables
      const formVariables = this.getFormVariables();
      console.log("Form variables for OptionalCriteriaManager:", formVariables);
      this.optionalCriteriaManager = new OptionalCriteriaManager(
        this.query,
        variable,
        queryLine,
        widget,
        formFieldDiv,
        formVariables,
      );

      // Add options like "Any value" and "Not Exist"
      this.addValuesAndOptions(formFieldDiv, queryLine, widget, variable);
    }
  }

  // Method to create a label with an SVG tooltip icon
  private createLabel(variable: string): HTMLLabelElement {
    const label = document.createElement("label");
    label.setAttribute("for", variable);
    label.classList.add("form-label");

    // Get the field label
    const labelText = SparnaturalFormI18n.getLabel(variable);
    label.innerHTML = `<strong>${labelText}</strong>`;

    // Create an SVG help icon
    const helpIcon = document.createElement("span");
    helpIcon.classList.add("help-icon");
    helpIcon.setAttribute("tabindex", "0");

    helpIcon.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
          <path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zM262.655 90c-54.497 0-89.255 22.957-116.549 63.758-3.536 5.286-2.353 12.415 2.715 16.258l34.699 26.31c5.205 3.947 12.621 3.008 16.665-2.122 17.864-22.658 30.113-35.797 57.303-35.797 20.429 0 45.698 13.148 45.698 32.958 0 14.976-12.363 22.667-32.534 33.976C247.128 238.528 216 254.941 216 296v4c0 6.627 5.373 12 12 12h56c6.627 0 12-5.373 12-12v-1.333c0-28.462 83.186-29.647 83.186-106.667 0-58.002-60.165-102-116.531-102zM256 338c-25.365 0-46 20.635-46 46 0 25.364 20.635 46 46 46s46-20.636 46-46c0-25.365-20.635-46-46-46z"/>
      </svg>`;

    // Get the help text if it exists on form config
    const helpText = SparnaturalFormI18n.getHelp(variable);
    if (helpText) {
      helpIcon.setAttribute(
        "data-tippy-content",
        helpText.replace(/"/g, "&quot;"),
      );
      label.appendChild(helpIcon);
    }
    return label;
  }

  //method to find the line in the query corresponding to the variable
  private findInBranches(
    branches: Branch[],
    variable: string,
  ): CriteriaLine | null {
    for (const branch of branches) {
      if (branch.line.o === variable) return branch.line;
      if (branch.children && branch.children.length > 0) {
        const result = this.findInBranches(branch.children, variable);
        if (result) return result;
      }
    }
    return null;
  }

  private createWidget(queryLine: CriteriaLine): AbstractWidget {
    const subject = queryLine.sType;
    const predicate = queryLine.p;
    const object = queryLine.oType;

    const specEntity = this.specProvider.getEntity(subject);
    const connectingProperty = this.specProvider.getProperty(predicate);

    const widget = this.widgetFactory.buildWidget(
      { variable: queryLine.s, type: specEntity.getId() },
      { variable: "predicate", type: connectingProperty.getId() },
      { variable: queryLine.o, type: object },
    );
    widget.render();
    // console.log("widget", widget);
    return widget;
  }

  //methode to add values and options to the widget in the form
  private addValuesAndOptions(
    formFieldDiv: HTMLElement,
    queryLine: CriteriaLine,
    widget: AbstractWidget,
    variable: string,
  ): void {
    const valueDisplay = document.createElement("div");
    valueDisplay.setAttribute("id", `selected-value-${variable}`);
    valueDisplay.classList.add("value-display-container");
    valueDisplay.style.marginTop = "5px";
    formFieldDiv.appendChild(valueDisplay);

    // Add a set to store selected values and a method to update the display

    // Save the original map open callback for MapWidget reset
    const mapOpenCallback = (widget as any).renderMapValueBtn?.callBack;
    const isMapWidget = widget.html[0].classList.contains("map-widget");

    // Helper to reset the MapWidget button back to "Ouvrir la carte" / "Open Map"
    const resetMapButton = () => {
      if (!isMapWidget) return;
      const mapBtn = (widget as any).renderMapValueBtn;
      if (mapBtn && mapOpenCallback) {
        mapBtn.callBack = mapOpenCallback;
        const buttonEl = mapBtn.html[0]?.querySelector(".button-add");
        if (buttonEl) {
          buttonEl.textContent = I18n.labels.MapWidgetOpenMap;
        }
      }
    };

    const selectedValues = new Set<any>();
    const updateValueDisplay = () => {
      valueDisplay.innerHTML = "";

      selectedValues.forEach((val) => {
        const valueContainer = document.createElement("div");
        valueContainer.classList.add("selected-value-container");
        const valueLabel = document.createElement("span");
        valueLabel.innerHTML = `${val.label}`;
        valueLabel.classList.add("selected-value-label");
        valueContainer.appendChild(valueLabel);

        const removeBtn = new UnselectBtn(widget, () => {
          selectedValues.delete(val);
          console.log(selectedValues);
          updateValueDisplay();
          queryLine.criterias = Array.from(selectedValues);
          console.log("QUERYLINE ", queryLine);

          // Clear widget's internal values (needed for MapWidget)
          if ((widget as any).widgetValues) {
            (widget as any).widgetValues = [];
          }

          // Reset MapWidget button to "Open Map" state
          resetMapButton();

          // Vide les champs input du widget
          const inputs = widget.html[0].querySelectorAll("input");
          inputs.forEach((input) => {
            (input as HTMLInputElement).value = "";
          });
          formFieldDiv.dispatchEvent(
            new CustomEvent("valueRemoved", {
              bubbles: true,
              detail: { value: val, variable: variable },
            }),
          );

          // Update options visibility
          if (this.optionalCriteriaManager) {
            this.optionalCriteriaManager.updateOptionVisibility();
          }
        }).render();
        valueContainer.appendChild(removeBtn.html[0]);
        valueDisplay.appendChild(valueContainer);
      });
    };

    // Add existing values to the widget

    // Add an event listener to add values to the widget
    widget.html[0].addEventListener("renderWidgetVal", (e: CustomEvent) => {
      //console.log("widget", widget);
      console.log("e.detail", e.detail);

      let valueToInject: any[];

      // Handle different cases for e.detail
      if (Array.isArray(e.detail)) {
        // Case: e.detail is an array
        valueToInject = e.detail.map((item: any) => item);
        console.log("here");
        typeof valueToInject[0] === "string";
      } else if (e.detail.criteria) {
        // Case: e.detail contains a single value or a wrapped object
        valueToInject = Array.isArray(e.detail) ? e.detail : [e.detail];
      } else {
        console.warn("Unexpected e.detail format:", e.detail);
        return; // Exit early if the format is not recognized
      }

      console.log("valueToInject", valueToInject);

      valueToInject.forEach((val: any) => {
        // Check if same value already exists (skip silently, e.g. map close re-fires)
        const existingValue = Array.from(selectedValues).find(
          (existingVal: any) => existingVal.label === val.label,
        );
        if (existingValue) {
          // Close the map wrapper if present (map close re-fire case)
          const mapWrapper = widget.html[0].querySelector(".map-wrapper");
          if (mapWrapper) {
            mapWrapper.remove();
          }
          resetMapButton();
          return; // Same value, skip
        }

        const isSingleValue = widget.valueRepetition === ValueRepetition.SINGLE;

        // Si SINGLE et une valeur déjà existante → Afficher warning
        if (isSingleValue && selectedValues.size > 0) {
          // Créer un message d'alerte temporaire
          let warningMsg = formFieldDiv.querySelector(
            ".single-value-warning",
          ) as HTMLElement;
          if (!warningMsg) {
            warningMsg = document.createElement("div");
            warningMsg.classList.add("single-value-warning");
            warningMsg.innerText = I18nForm.labels["messageSingleValue"];
            warningMsg.style.color = "red";
            warningMsg.style.fontSize = "13px";
            formFieldDiv.insertBefore(warningMsg, valueDisplay);

            // Supprimer automatiquement après 5 secondes
            setTimeout(() => {
              warningMsg.remove();
            }, 5000);
          }
          return; // Ne pas ajouter la nouvelle valeur
        }

        selectedValues.add(val);

        updateValueDisplay();
        queryLine.criterias = Array.from(selectedValues);

        // Close the map wrapper after value is accepted
        const mapWrapper = widget.html[0].querySelector(".map-wrapper");
        if (mapWrapper) {
          mapWrapper.remove();
        }
        resetMapButton();

        formFieldDiv.dispatchEvent(
          new CustomEvent("valueAdded", {
            bubbles: true,
            detail: { value: val, variable: variable },
          }),
        );

        if (this.optionalCriteriaManager) {
          this.optionalCriteriaManager.updateOptionVisibility();
        }
      });
    });

    // Add AnyValue and NotExist options
    const formVariables = this.getFormVariables();
    this.optionalCriteriaManager = new OptionalCriteriaManager(
      this.query,
      variable,
      queryLine,
      widget,
      formFieldDiv,
      formVariables,
    );
  }
}
export default FormField;
